import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { hasPerm, routeToModule } from "@/lib/permissions";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import CreatePartsRequest from "@/pages/CreatePartsRequest";
import AllOrders from "@/pages/AllOrders";
import Workbench from "@/pages/Workbench";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import NewPackingSlip from "@/pages/NewPackingSlip";
import SavedPackingSlips from "@/pages/SavedPackingSlips";
import PrintPackingSlip from "@/pages/PrintPackingSlip";
import StockPlanning from "@/pages/StockPlanning";
import AvDashboard from "@/pages/av/AvDashboard";
import NewAv from "@/pages/av/NewAv";
import AvWorkbench from "@/pages/av/AvWorkbench";
import AvReports from "@/pages/av/AvReports";
import AvDetail from "@/pages/av/AvDetail";
import PvDashboard from "@/pages/pv/PvDashboard";
import PvWorkbench from "@/pages/pv/PvWorkbench";
import PvUpload from "@/pages/pv/PvUpload";
import PvStatusMaster from "@/pages/pv/PvStatusMaster";
import PvReports from "@/pages/pv/PvReports";
import { SecurityScan, SecurityAwaiting, SecurityHistory } from "@/pages/security/Security";
import LocTracker from "@/pages/loc/LocTracker";
import LocCaseDetail from "@/pages/loc/LocCaseDetail";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";

const Protected = ({ children }) => {
  const { currentUser, loaded } = useApp();
  const location = useLocation();
  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center text-yamaha-700 font-medium">
        Loading IVA Motors Service Sync...
      </div>
    );
  }
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Force-redirect users on a temporary password into the change-password
  // page until they update it. Only exception: the change-password page itself.
  if (currentUser.must_change_password && location.pathname !== "/account/password") {
    return <Navigate to="/account/password" replace />;
  }
  return children;
};

const PublicOnly = ({ children }) => {
  const { currentUser, loaded } = useApp();
  if (!loaded) return null;
  if (currentUser) {
    const target = currentUser.must_change_password ? "/account/password" : "/";
    return <Navigate to={target} replace />;
  }
  return children;
};

/**
 * Find the first module route the user can view — used as a safe landing
 * page when a user has no dashboard access.
 */
const findFirstAllowedRoute = (user) => {
  if (!user) return "/login";
  if (user.role === "admin") return "/";
  const p = user.module_permissions || {};
  if (p.dashboard?.view) return "/";
  if (p.spares?.view) return "/workbench";
  if (p.reports?.view) return "/orders";
  if (p.av?.view) return "/av/dashboard";
  if (p.pv?.view) return "/pv/dashboard";
  if (p.loc?.view) return "/loc";
  if (p.masters?.view) return "/masters/pv-status";
  if (p.security?.view) return "/security/scan";
  return null;
};

/**
 * ModuleGuard — enforces module-level view (and optional add) permission
 * from the Admin → Module Access matrix. If denied, redirects to a safe
 * landing page (or an Access Denied screen when nothing is allowed).
 */
const ModuleGuard = ({ module: mod, action = "view", children }) => {
  const { currentUser } = useApp();
  const location = useLocation();
  const moduleKey = mod || routeToModule(location.pathname);
  if (!moduleKey) return children;
  if (hasPerm(currentUser, moduleKey, action)) return children;

  const target = findFirstAllowedRoute(currentUser);
  // Fire a friendly toast only when the user actually TRIED to open this
  // page (i.e. they explicitly navigated to a specific module). Auto-landings
  // on "/" (post-login redirect) are silent to avoid confusing the guard.
  const isAutoLanding = location.pathname === "/" && moduleKey === "dashboard";
  if (target && target !== location.pathname) {
    if (!isAutoLanding) {
      const msg = action === "view"
        ? "You do not have access to this module."
        : `You do not have permission to ${action} in this module.`;
      // Dedupe rapid duplicate toasts (StrictMode double-render + Guard rechecks).
      setTimeout(() => toast.error(msg, { id: `no-access-${moduleKey}-${action}` }), 0);
    }
    return <Navigate to={target} replace />;
  }
  // No accessible module — show a friendly landing (do NOT redirect in a loop).
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-8 max-w-md text-center space-y-3">
        <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">!</div>
        <div className="text-[16px] font-semibold text-[#1a1b22]">No Modules Available</div>
        <div className="text-[13px] text-[#747684]">
          Your role does not currently have access to any module. Please contact the administrator.
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnly>
                <Login />
              </PublicOnly>
            }
          />
          <Route
            path="/account/password"
            element={
              <Protected>
                <ChangePassword />
              </Protected>
            }
          />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route path="/" element={<ModuleGuard module="dashboard"><Dashboard /></ModuleGuard>} />
            <Route path="/create" element={<ModuleGuard module="spares" action="add"><CreatePartsRequest /></ModuleGuard>} />
            <Route path="/orders" element={<ModuleGuard module="reports"><AllOrders /></ModuleGuard>} />
            <Route path="/workbench" element={<ModuleGuard module="spares"><Workbench /></ModuleGuard>} />
            <Route path="/reports" element={<ModuleGuard module="reports"><Reports /></ModuleGuard>} />
            <Route path="/packing-slip/new" element={<ModuleGuard module="spares" action="add"><NewPackingSlip /></ModuleGuard>} />
            <Route path="/packing-slips" element={<ModuleGuard module="reports"><SavedPackingSlips /></ModuleGuard>} />
            <Route path="/stock-planning" element={<ModuleGuard module="spares"><StockPlanning /></ModuleGuard>} />
            <Route path="/av/dashboard" element={<ModuleGuard module="av"><AvDashboard /></ModuleGuard>} />
            <Route path="/av/new" element={<ModuleGuard module="av" action="add"><NewAv /></ModuleGuard>} />
            <Route path="/av/workbench" element={<ModuleGuard module="av"><AvWorkbench /></ModuleGuard>} />
            <Route path="/av/reports" element={<ModuleGuard module="av"><AvReports /></ModuleGuard>} />
            <Route path="/av/:id" element={<ModuleGuard module="av"><AvDetail /></ModuleGuard>} />
            <Route path="/pv/dashboard" element={<ModuleGuard module="pv"><PvDashboard /></ModuleGuard>} />
            <Route path="/pv/workbench" element={<ModuleGuard module="pv"><PvWorkbench /></ModuleGuard>} />
            <Route path="/pv/upload" element={<ModuleGuard module="pv" action="add"><PvUpload /></ModuleGuard>} />
            <Route path="/pv/reports" element={<ModuleGuard module="pv"><PvReports /></ModuleGuard>} />
            <Route path="/loc" element={<ModuleGuard module="loc"><LocTracker /></ModuleGuard>} />
            <Route path="/loc/:id" element={<ModuleGuard module="loc"><LocCaseDetail /></ModuleGuard>} />
            <Route path="/security/scan" element={<ModuleGuard module="security"><SecurityScan /></ModuleGuard>} />
            <Route path="/security/awaiting" element={<ModuleGuard module="security"><SecurityAwaiting /></ModuleGuard>} />
            <Route path="/security/history" element={<ModuleGuard module="security"><SecurityHistory /></ModuleGuard>} />
            <Route path="/masters/pv-status" element={<ModuleGuard module="masters"><PvStatusMaster /></ModuleGuard>} />
            <Route path="/admin" element={<Admin />} />
          </Route>
          <Route
            path="/packing-slip/:id/print"
            element={
              <Protected>
                <PrintPackingSlip />
              </Protected>
            }
          />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
